<template>
	<common-dialog ref="failedRequestDialog" @closed="close">
		<template #header>
			KFI Request Failed
		</template>
		<template #body>
			<template v-if="product">
				<p class="mb-5 text-body-2">
					KFI requests can fail for a variety of reasons, most of which are easily overcome by speaking with our support team.
					<span v-if="isApi">
						Occasionally, API enabled requests can fail because of a service timeout. In these instances, clicking to resubmit the request will usually result in a successful response.
						<strong>
							The most common cause of a failed KFI request is missing/invalid API credentials. We would advise you to <a target="_blank" href="/settings/credentials">check your {{ product.product.provider.name }} credentials are accurate</a> and, if appropriate, click to resubmit this request.
						</strong>
					</span>
				</p>

				<v-item-group>
					<v-row>
						<v-col cols="12" sm="6" class="py-0">
							<v-item>
								<v-card
									:disabled="resubmitDisabled"
									class="text-h6 font-weight-regular pa-6 text-center align-content-center grey lighten-2"
									height="100%"
									@click="resubmitKfiRequest"
								>
									<template v-if="resubmitting">
										<strong class="d-block mb-3">Resubmitting your KFI request</strong>
										<v-progress-linear indeterminate />
									</template>
									<template v-else-if="isProcessing">
										<strong class="d-block mb-3">Processing KFI request</strong>
										<v-progress-linear indeterminate />
									</template>
									<template v-else-if="maxResubmitAttemptsReached">
										<span class="font-weight-medium error--text">
											We are unable to resubmit this request. Please contact our support team
										</span>
									</template>
									<template v-else>
										<span class="primary">Click here</span> to <strong>attempt to immediately resubmit</strong> your KFI request
									</template>
								</v-card>
							</v-item>
						</v-col>
						<v-col cols="12" sm="6" class="py-0">
							<v-item>
								<v-card class="text-h6 font-weight-regular pa-6 text-center grey lighten-2" height="100%" @click="openChat">
									<span class="accent">Click here</span> to <strong>open up a chat session</strong> with our support team
								</v-card>
							</v-item>
						</v-col>
					</v-row>
				</v-item-group>
			</template>
		</template>
	</common-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import { EventBus, ElementTools } from '@/utils';
import CommonDialog from '@/component/common/dialog';

export default {
	name: 'app-kfi-request-failed-request-resubmit',

	components: {
		CommonDialog
	},

	props: {
		product: { type: Object, required: true },
		isProcessing: { type: Boolean, required: true },
		maxResubmitAttempts: { type: Number, required: true, default: 3 }
	},

	data() {
		return {
			resubmitting: false
		};
	},

	computed: {
		maxResubmitAttemptsReached() {
			return this.product?.retryCount >= this.maxResubmitAttempts;
		},

		resubmitDisabled() {
			return this.resubmitting || this.maxResubmitAttemptsReached || this.isProcessing;
		},

		isApi() {
			return this.product?.product?.sqs;
		}
	},

	methods: {
		...mapActions('LifetimeMortgageKfi', ['sendKfiBackToQueue']),

		open() {
			this.$refs.failedRequestDialog.open();
		},

		close() {
			this.$refs.failedRequestDialog.close();
			this.$emit('dialog-closed');
		},

		resubmitKfiRequest() {
			this.resubmitting = true;
			this.sendKfiBackToQueue(this.product.id)
				.then((res) => {
					this.$emit('update:product', {
						...this.product,
						status: 'KFI Request Submitted',
						retryCount: res.data.retryCount
					});
				})
				.catch((err) => {
					ElementTools.fireNotification(this.$el, 'error', err.data?.message || 'Failed to send KFI back to queue');
				})
				.finally(() => {
					this.resubmitting = false;
                    this.close();
				});
		},

		openChat() {
			EventBus.$emit('open-chat');
		}
	}
};
</script>
